import WeatherHealthBadge from "./WeatherHealthBadge";
import { formatDateTime } from "../../users/adminUserFormatters";
import { getWeatherSourceCategoryLabel } from "../../../../../constants/colombiaWeatherSourceMetadata.js";
import { getWeatherSourceAdminDescription } from "../../../../../weather/weatherSourceActionRegistry.js";

function MetricRow({ label, value }) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return (
    <div className="weather-source-detail__metric">
      <span className="weather-source-detail__metric-label">{label}</span>
      <span className="weather-source-detail__metric-value">
        {typeof value === "object" ? JSON.stringify(value) : String(value)}
      </span>
    </div>
  );
}

function renderSourceMetrics(source) {
  const metrics = source?.sourceMetrics;
  if (!metrics) {
    return <p className="weather-source-detail__empty">No source-specific metrics available.</p>;
  }

  const entries = Object.entries(metrics).filter(
    ([, value]) => value !== null && value !== undefined
  );

  if (!entries.length) {
    return <p className="weather-source-detail__empty">No source-specific metrics available.</p>;
  }

  return entries.map(([key, value]) => (
    <MetricRow
      key={key}
      label={key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}
      value={
        Array.isArray(value)
          ? value
              .map((item) =>
                typeof item === "object" ? Object.values(item).join(": ") : String(item)
              )
              .join(", ")
          : typeof value === "object"
            ? JSON.stringify(value)
            : value
      }
    />
  ));
}

const WeatherLayerHealthPanel = ({ source, sourceAlerts = [], monitoringRun = null, onClose }) => {
  if (!source) {
    return null;
  }

  const adminDescription =
    source.guidance ||
    source.message ||
    getWeatherSourceAdminDescription(source.sourceKey);

  return (
    <aside className="weather-source-detail panel-card">
      <header className="weather-source-detail__header">
        <div>
          <h2>{source.sourceName}</h2>
          <p className="weather-source-detail__key">{source.sourceKey}</p>
        </div>
        <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="weather-source-detail__status-row">
        <WeatherHealthBadge status={source.healthStatus} />
        {source.sourceCategoryLabel || getWeatherSourceCategoryLabel(source.sourceKey) ? (
          <span className="weather-source-detail__tag">
            {source.sourceCategoryLabel || getWeatherSourceCategoryLabel(source.sourceKey)}
          </span>
        ) : null}
        {source.isExperimental ? (
          <span className="weather-source-detail__tag">Experimental</span>
        ) : null}
        {source.isHistorical ? (
          <span className="weather-source-detail__tag weather-source-detail__tag--archive">
            Historical
          </span>
        ) : null}
      </div>

      {adminDescription ? (
        <p className="weather-source-detail__message">{adminDescription}</p>
      ) : null}

      {source.documentationPath ? (
        <p className="weather-source-detail__docs">
          Documentation: <code>{source.documentationPath}</code>
        </p>
      ) : null}

      {source.sourceReadiness ? (
        <section className="weather-source-detail__section">
          <h3>Live source configuration</h3>
          <MetricRow label="Readiness status" value={source.sourceReadiness.status} />
      <MetricRow
        label="Last health check"
        value={source.sourceMetrics?.healthCheckStatus || "Not run yet"}
      />
      <MetricRow
        label="Last check response time"
        value={
          source.sourceMetrics?.lastHealthCheckResponseTimeMs != null
            ? `${source.sourceMetrics.lastHealthCheckResponseTimeMs} ms`
            : "—"
        }
      />
          <MetricRow label="Configured" value={source.sourceReadiness.isConfigured ? "Yes" : "No"} />
          <MetricRow
            label="Experimental enabled"
            value={source.sourceReadiness.experimentalEnabled ? "Yes" : "No"}
          />
          <MetricRow label="URL configured" value={source.sourceReadiness.urlConfigured ? "Yes" : "No"} />
          <MetricRow label="Layer id" value={source.sourceReadiness.layerId || "—"} />
          <MetricRow label="Timeout (ms)" value={source.sourceReadiness.timeoutMs} />
          <MetricRow
            label="Missing config"
            value={
              source.sourceReadiness.missingConfig?.length
                ? source.sourceReadiness.missingConfig.join(", ")
                : "None"
            }
          />
          {source.sourceReadiness.warnings?.length ? (
            <MetricRow label="Warnings" value={source.sourceReadiness.warnings.join("; ")} />
          ) : null}
          {source.sourceReadiness.message ? (
            <p className="weather-source-detail__message">{source.sourceReadiness.message}</p>
          ) : null}
        </section>
      ) : null}

      <section className="weather-source-detail__section">
        <h3>Attribution &amp; product copy</h3>
        <MetricRow label="UI label" value={source.uiLabel} />
        <MetricRow label="Product identity" value={source.productIdentityStatus} />
        <MetricRow label="Official / third-party" value={
          source.isThirdPartyBackup
            ? "Third-party backup"
            : source.isOfficialSource
              ? "Official IDEAM source"
              : source.isOfficial
                ? "Official"
                : "Third-party"
        } />
        <MetricRow label="Attribution" value={source.attributionText} />
        <MetricRow label="License notes" value={source.licenseNotes} />
        <MetricRow label="License / validation doc" value={source.licenseDocPath} />
        <MetricRow label="Validation status" value={source.validationStatus} />
        <MetricRow label="Active candidate key" value={source.currentCandidateKey} />
        <MetricRow label="Experimental" value={source.isExperimental ? "Yes" : "No"} />
        <MetricRow label="Applied production decision" value={source.appliedProductionDecision} />
        <MetricRow label="Production label" value={source.productionLabel} />
        <MetricRow label="Promotion blocked" value={source.promotionBlocked ? "Yes" : "No"} />
        <MetricRow label="RainViewer fallback enabled" value={source.allowRainViewerFallback === false ? "No" : "Yes"} />
        <MetricRow label="Decision record" value={source.productionDecisionRecordPath} />
        <MetricRow label="Applied decision log" value={source.appliedProductionDecisionRecordPath} />
        {source.configWarnings?.length ? (
          <MetricRow label="Config warnings" value={source.configWarnings.join("; ")} />
        ) : null}
      </section>

      {source.radarTimeline ? (
        <section className="weather-source-detail__section">
          <h3>Radar timeline</h3>
          <MetricRow label="Animation available" value={source.radarTimeline.animationAvailable ? "Yes" : "No"} />
          <MetricRow label="Frame count" value={source.radarTimeline.frameCount} />
          <MetricRow label="Latest frame time" value={source.radarTimeline.latestFrameTime || "—"} />
          <MetricRow label="Time metadata source" value={source.radarTimeline.timeMetadataSource} />
          <MetricRow label="Timeline endpoint" value={source.radarTimeline.endpoint} />
          {source.radarTimeline.preferredEndpoint ? (
            <MetricRow label="Preferred timeline" value={source.radarTimeline.preferredEndpoint} />
          ) : null}
          {source.radarTimeline.reason ? (
            <MetricRow label="Timeline note" value={source.radarTimeline.reason} />
          ) : null}
        </section>
      ) : null}

      {source.tileCache ? (
        <section className="weather-source-detail__section">
          <h3>Tile cache</h3>
          <MetricRow label="Cache enabled" value={source.tileCache.enabled ? "Yes" : "No"} />
          <MetricRow label="Storage mode" value={source.tileCache.storageMode} />
          <MetricRow label="TTL (seconds)" value={source.tileCache.ttlSeconds} />
          <MetricRow label="Entry count" value={source.tileCache.entryCount} />
          <MetricRow label="Hits" value={source.tileCache.hits} />
          <MetricRow label="Misses" value={source.tileCache.misses} />
          <MetricRow label="Stale served" value={source.tileCache.staleServed} />
          <MetricRow label="Evictions" value={source.tileCache.evictions} />
          <MetricRow label="Last cleared" value={source.tileCache.lastClearedAt || "—"} />
          <MetricRow label="Proxy active" value={source.tileCache.proxyActive ? "Yes" : "No"} />
        </section>
      ) : null}

      {sourceAlerts?.length ? (
        <section className="weather-source-detail__section">
          <h3>Active alerts ({sourceAlerts.length})</h3>
          <ul className="weather-source-detail__layer-list">
            {sourceAlerts.map((alert) => (
              <li key={alert.alertId}>
                <strong>{alert.title}</strong>
                <WeatherHealthBadge status={alert.severity} />
                <span>{alert.alertType}</span>
                <p className="weather-source-detail__message">{alert.message}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {monitoringRun ? (
        <section className="weather-source-detail__section">
          <h3>Latest monitoring run</h3>
          <MetricRow label="Run type" value={monitoringRun.runType} />
          <MetricRow label="Status" value={monitoringRun.status} />
          <MetricRow label="Started" value={formatDateTime(monitoringRun.startedAt)} />
          <MetricRow
            label="Fallback active"
            value={monitoringRun.metadata?.fallbackActive ? "Yes" : "No"}
          />
        </section>
      ) : null}

      {source.fallback?.fallbackSourceKey ? (
        <section className="weather-source-detail__section">
          <h3>Fallback</h3>
          <p>
            Fallback source: <strong>{source.fallback.fallbackSourceKey}</strong>
          </p>
          <p>
            Status: {source.fallback.fallbackActive ? "Active" : "Standby"}
            {source.fallback.reason ? ` (${source.fallback.reason})` : ""}
          </p>
        </section>
      ) : null}

      <section className="weather-source-detail__section">
        <h3>Health</h3>
        <MetricRow label="Last checked" value={formatDateTime(source.lastCheckedAt)} />
        <MetricRow label="Last success" value={formatDateTime(source.lastSuccessAt)} />
        <MetricRow label="Last observed data" value={formatDateTime(source.lastObservedDataAt)} />
        <MetricRow label="Freshness (minutes)" value={source.freshnessMinutes} />
      </section>

      <section className="weather-source-detail__section">
        <h3>Layers ({source.layers?.length || 0})</h3>
        {!source.layers?.length ? (
          <p className="weather-source-detail__empty">No layers configured for this source.</p>
        ) : (
          <ul className="weather-source-detail__layer-list">
            {source.layers.map((layer) => (
              <li key={layer.layerKey}>
                <strong>{layer.layerName || layer.layerKey}</strong>
                <span>{layer.mapboxLayerGroup || layer.layerType}</span>
                <WeatherHealthBadge status={layer.healthStatus || "unknown"} />
                {!layer.isEnabled ? <span>Disabled</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="weather-source-detail__section">
        <h3>Source metrics</h3>
        {renderSourceMetrics(source)}
      </section>

      {source.latestRun ? (
        <section className="weather-source-detail__section">
          <h3>Latest run</h3>
          <MetricRow label="Type" value={source.latestRun.runType} />
          <MetricRow label="Status" value={source.latestRun.status} />
          <MetricRow label="Started" value={formatDateTime(source.latestRun.startedAt)} />
          <MetricRow label="Completed" value={formatDateTime(source.latestRun.completedAt)} />
          <MetricRow label="Records found" value={source.latestRun.recordsFound} />
          <MetricRow label="Inserted" value={source.latestRun.recordsInserted} />
          <MetricRow label="Updated" value={source.latestRun.recordsUpdated} />
          {source.latestRun.errorMessage ? (
            <p className="weather-source-detail__error">{source.latestRun.errorMessage}</p>
          ) : null}
        </section>
      ) : (
        <section className="weather-source-detail__section">
          <h3>Latest run</h3>
          <p className="weather-source-detail__empty">No runs recorded yet.</p>
        </section>
      )}

      {source.recentErrors?.length ? (
        <section className="weather-source-detail__section">
          <h3>Recent errors</h3>
          <ul className="weather-source-detail__error-list">
            {source.recentErrors.map((entry) => (
              <li key={entry.runId}>
                <strong>{entry.runType}</strong> — {entry.status}
                <div>{formatDateTime(entry.completedAt || entry.startedAt)}</div>
                {entry.errorMessage ? <div>{entry.errorMessage}</div> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </aside>
  );
};

export default WeatherLayerHealthPanel;
